import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { setAuthToken, setUnauthorizedHandler } from '../api/client';

export const AuthContext = createContext(null);

const STORAGE_KEY = 'token';

const emptyAuth = () => ({ token: null, userName: '', userId: '', expiresAt: 0 });

const readToken = (token) => {
  const decoded = jwtDecode(token);

  return {
    token,
    userName: decoded?.user?.nome || '',
    userId: decoded?.user?.id || '',
    // exp vem em segundos; guardamos em milissegundos.
    expiresAt: typeof decoded?.exp === 'number' ? decoded.exp * 1000 : 0,
  };
};

const isExpired = (expiresAt) => Boolean(expiresAt) && expiresAt <= Date.now();

const loadStoredAuth = () => {
  const storedToken = localStorage.getItem(STORAGE_KEY);

  if (!storedToken) {
    return emptyAuth();
  }

  try {
    const authData = readToken(storedToken);

    if (isExpired(authData.expiresAt)) {
      localStorage.removeItem(STORAGE_KEY);
      return emptyAuth();
    }

    setAuthToken(authData.token);
    return authData;
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY);
    return emptyAuth();
  }
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(loadStoredAuth);
  const [sessionMessage, setSessionMessage] = useState('');
  const { token, expiresAt } = authData;

  const logout = useCallback((message = '') => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthToken(null);
    setSessionMessage(message);
    setAuthData(emptyAuth());
  }, []);

  const login = useCallback((newToken) => {
    const nextAuth = readToken(newToken);
    localStorage.setItem(STORAGE_KEY, newToken);
    setAuthToken(newToken);
    setSessionMessage('');
    setAuthData(nextAuth);
  }, []);

  const updateUserName = useCallback((newName) => {
    setAuthData((prev) => ({ ...prev, userName: newName }));
  }, []);

  const clearSessionMessage = useCallback(() => setSessionMessage(''), []);

  // Mantém o token do axios em sincronia mesmo em recarregamentos rápidos.
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Encerra a sessão assim que o token expirar, sem esperar um erro da API.
  useEffect(() => {
    if (!token || !expiresAt) {
      return undefined;
    }

    const remaining = expiresAt - Date.now();

    if (remaining <= 0) {
      logout('Sua sessão expirou. Entre novamente.');
      return undefined;
    }

    const timer = setTimeout(
      () => logout('Sua sessão expirou. Entre novamente.'),
      // setTimeout satura acima de ~24,8 dias; o token dura 2 dias.
      Math.min(remaining, 2147483647)
    );

    return () => clearTimeout(timer);
  }, [token, expiresAt, logout]);

  // A API recusando o token também derruba a sessão.
  useEffect(() => {
    setUnauthorizedHandler(() => logout('Sua sessão expirou. Entre novamente.'));
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo(
    () => ({
      authData,
      isAuthenticated: Boolean(token),
      sessionMessage,
      clearSessionMessage,
      login,
      logout,
      updateUserName,
    }),
    [authData, token, sessionMessage, clearSessionMessage, login, logout, updateUserName]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth precisa estar dentro de AuthProvider');
  }

  return context;
};
